import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { Fragment } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { SDKContext } from '../context';
import { fireBeacon, applyCacheBuster, injectMacros, injectMacrosText, expandCouDescriptionMacrosToRawHtml, collectRawMacroKeys, withCreativeTypeImageMacros, withPublisherImageMacros, } from '../utils';
import { couActionSelector, filterToAllowedCouMacros, } from '../utils/macroAllowlist';
import { isPerksWalletEligible, perksWalletButtonText, savePerksWalletOffer, usesPwaasSave, } from '../utils/perksWallet';
import { morphInnerHTML } from '../utils/domMorph';
import { applyProgressRegions, planProgressRegions, progressJumpTarget, progressKeyJumpTarget, releaseProgressRegions, teardownProgressRegions, } from '../utils/couProgress';
import { useIsMobile } from '../utils/responsive';
import { extractImageUrls, preloadImages } from '../utils/imagePreload';
import { buildOfferwallUrl } from '../utils/offerwallUrl';
import { useIsInView } from '../utils/useIsInView';
import { PLATFORM_MESSAGES } from '../constants';
import { OfferModal } from './OfferModal';
import { resolveBundledCustomLayout } from '../customLayoutPresets/resolveBundledCustomLayout';
import { createDebugLogger } from '../utils/debug';
const debug = createDebugLogger('CustomOfferUnit');
function effectiveCustomLayout(sdk, settings) {
    return resolveBundledCustomLayout(sdk.customLayout, settings);
}
/**
 * For a sink whose raw/escaped usage cannot be read off a template — a publisher
 * `customLayout.render` that builds its own HTML. Every value is escaped, since
 * nothing downstream is known to.
 */
const EVERY_KEY_EMITS_RAW_HTML = () => true;
/** Maps `styles.header` strings into COU macros (SOU/MOU header bar + lead-in). */
export function publisherHeaderMacros(styles, userAttributes, emitsRawHtml) {
    const header = styles?.header;
    if (!header) {
        return {};
    }
    const out = {};
    /**
     * Resolved per destination key, not per source string: `text` and `header_text`
     * share one source but the template can emit one raw and the other escaped.
     */
    const resolveFor = (key, raw) => emitsRawHtml(key)
        ? injectMacros(raw, userAttributes)
        : injectMacrosText(raw, userAttributes);
    const textRaw = header.text;
    if (typeof textRaw === 'string' && textRaw.trim()) {
        out.text = resolveFor('text', textRaw);
        /** Same source as OfferHeader title — `{{header_text}}` is its macro alias. */
        out.header_text = resolveFor('header_text', textRaw);
    }
    const leadInRaw = header.lead_in_text;
    if (typeof leadInRaw === 'string' && leadInRaw.trim()) {
        out.lead_in_text = resolveFor('lead_in_text', leadInRaw);
        /** Same source as the SOU/MOU `#adpx_lead-in` line. */
        out.lead_in = resolveFor('lead_in', leadInRaw);
    }
    /** Explicit same-named field wins over the `header.text` alias above. */
    const headerTextRaw = header.header_text;
    if (typeof headerTextRaw === 'string' && headerTextRaw.trim()) {
        out.header_text = resolveFor('header_text', headerTextRaw);
    }
    for (const key of ['eyebrow', 'subhead']) {
        const raw = header[key];
        if (typeof raw !== 'string' || !raw.trim()) {
            continue;
        }
        out[key] = resolveFor(key, raw);
    }
    return out;
}
/**
 * Pre-resolves `{{user_macro}}` occurrences inside offer/campaign field values, before
 * the template's own macro pass consumes them.
 *
 * Escaping here depends on how the template emits the key. A key the template
 * substitutes with `{{key}}` resolves unescaped, because the template pass escapes it —
 * escaping in both passes is what produced `o&#39;blah` on screen (TASK-1496). A key
 * the template emits raw (`{{{key}}}`, which `expandCouDescriptionMacrosToRawHtml`
 * also rewrites the description fields into) has to be escaped here, because nothing
 * escapes it later.
 */
export function resolveUserMacrosInRecord(record, userAttributes, emitsRawHtml) {
    const out = {};
    for (const [key, val] of Object.entries(record)) {
        if (typeof val !== 'string') {
            out[key] = val;
            continue;
        }
        out[key] = emitsRawHtml(key)
            ? injectMacros(val, userAttributes)
            : injectMacrosText(val, userAttributes);
    }
    return out;
}
/**
 * Builds the flat macro namespace for one offer render.
 *
 * The offer + campaign layer is gated by `filterToAllowedCouMacros` so a template
 * cannot resolve MomentScience-internal campaign fields (issue #55). The publisher's
 * own layers — `styles.header` copy and `userAttributes` — stay ungated.
 */
export function macroAttrsForOffer(offer, userAttributes, publisherStyles, emitsRawHtml) {
    const { campaign, ...rest } = offer;
    const campaignAttrs = campaign != null ? { ...campaign } : {};
    const offerNamespace = {
        ...resolveUserMacrosInRecord(campaignAttrs, userAttributes, emitsRawHtml),
        ...resolveUserMacrosInRecord(rest, userAttributes, emitsRawHtml),
    };
    /**
     * The image macros derive from `campaign_images` / `publisher_campaign_images`,
     * which are not themselves resolvable — derive first, gate after, so the derived
     * URLs survive while their source arrays do not. `userAttributes` is layered over
     * the offer here because the derivation used to read the fully merged map, where
     * a same-named attribute won.
     */
    const imageSources = { ...offerNamespace, ...userAttributes };
    const imageMacros = withPublisherImageMacros(withCreativeTypeImageMacros({
        campaign_images: imageSources.campaign_images,
        publisher_campaign_images: imageSources.publisher_campaign_images,
    }));
    return {
        ...publisherHeaderMacros(publisherStyles, userAttributes, emitsRawHtml),
        ...filterToAllowedCouMacros(offerNamespace),
        ...userAttributes,
        ...filterToAllowedCouMacros(imageMacros),
    };
}
/** Resolve HTML template: effective `template` wins, then selector, then API `data.template`. */
function resolveMarkupTemplate(sdk, settings) {
    const layout = effectiveCustomLayout(sdk, settings);
    const fromInit = layout?.template?.trim();
    if (fromInit) {
        return fromInit;
    }
    const selector = layout?.templateSelector?.trim();
    if (selector && typeof document !== 'undefined') {
        const el = document.querySelector(selector);
        if (el instanceof HTMLTemplateElement) {
            const html = el.innerHTML.trim();
            return html || null;
        }
        if (el) {
            const html = el.innerHTML.trim();
            return html || null;
        }
    }
    const fromApi = sdk.lastResponseTemplate?.trim();
    if (fromApi) {
        return fromApi;
    }
    return null;
}
function couShouldOpen(sdk, storeSettings, storeOffers, storeStyles) {
    if (!storeSettings?.is_cou || storeOffers.length === 0 || !storeStyles) {
        return false;
    }
    const layout = effectiveCustomLayout(sdk, storeSettings);
    if (layout?.render) {
        return true;
    }
    return resolveMarkupTemplate(sdk, storeSettings) != null;
}
/**
 * Hides prev/next elements at sequence boundaries — automatic, no template
 * changes required. Each element's own `data-ms-loop="true"` attribute (set by
 * the publisher in their template) decides whether *that* button wraps instead
 * of hiding at the boundary. Must run after every `morphInnerHTML` call: morph
 * strips any live-DOM attribute (including an inline `style` set here) that
 * isn't present on the freshly-rendered markup, so a one-time toggle would be
 * wiped out on the very next render.
 */
function applyOfferNavVisibility(root, currentIndex, totalOffers) {
    root.querySelectorAll('[data-ms-action="prev"]').forEach(el => {
        const loop = el.getAttribute('data-ms-loop') === 'true';
        const hasPrevious = loop ? totalOffers > 1 : currentIndex > 0;
        el.style.display = hasPrevious ? '' : 'none';
    });
    root.querySelectorAll('[data-ms-action="next"]').forEach(el => {
        const loop = el.getAttribute('data-ms-loop') === 'true';
        const hasNext = loop ? totalOffers > 1 : currentIndex < totalOffers - 1;
        el.style.display = hasNext ? '' : 'none';
    });
}
/**
 * Drives every `data-ms-action="save-for-later"` element in the template: hides it
 * when the offer is not savable, otherwise owns its label and disabled state so a
 * theme gets the same copy and the same "Saved" state SOU/MOU render through
 * `PerksWallet`. Like `applyOfferNavVisibility` this must run after every
 * `morphInnerHTML` — morph restores the element from the template markup, which
 * carries neither the resolved label nor the inline `display`.
 */
function applySaveForLaterState(root, offer, settings, userAttributes, state) {
    const nodes = root.querySelectorAll(couActionSelector('save-for-later'));
    if (nodes.length === 0) {
        return;
    }
    const eligible = isPerksWalletEligible(offer, settings, userAttributes);
    nodes.forEach(el => {
        if (!eligible) {
            el.style.display = 'none';
            return;
        }
        el.style.display = '';
        /** Guarded: an unconditional write tears down and rebuilds the text node on
         * every render of the unit, which would drop any selection inside the label. */
        const label = perksWalletButtonText(settings, state);
        if (el.textContent !== label) {
            el.textContent = label;
        }
        el.setAttribute('data-ms-saved', state === 'saved' ? 'true' : 'false');
        if (el instanceof HTMLButtonElement) {
            el.disabled = state === 'saved';
        }
    });
}
/**
 * Custom Offer Unit — publisher HTML template with macro placeholders and `data-ms-action` hooks.
 */
export function CustomOfferUnit() {
    const ctx = useContext(SDKContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [offers, setOffers] = useState([]);
    const [settings, setSettings] = useState(null);
    const [styles, setStyles] = useState(null);
    const [userAttributes, setUserAttributes] = useState({});
    const [pixelsFired, setPixelsFired] = useState(new Set());
    /** Save-for-later progress per offer id; absent means "not started". */
    const [saveStates, setSaveStates] = useState({});
    const rootRef = useRef(null);
    const isModalOpenRef = useRef(false);
    isModalOpenRef.current = isModalOpen;
    const isMobile = useIsMobile();
    /** Region plans + the per-offer unit resolver, both produced by the render effect. */
    const progressRef = useRef(null);
    /**
     * The COU root as of the last progress pass. `rootRef` is nulled by Preact before
     * an unmount cleanup can read it, and this one is not, so teardown has something
     * to unmount from.
     */
    const progressRootRef = useRef(null);
    useEffect(() => {
        if (!ctx) {
            return;
        }
        const { sdk, store } = ctx;
        const handleStoreChange = () => {
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            setOffers(storeOffers);
            setSettings(storeSettings);
            setStyles(storeStyles);
            const open = couShouldOpen(sdk, storeSettings, storeOffers, storeStyles);
            if (open && !sdk.autoShow) {
                return;
            }
            setIsModalOpen(open);
        };
        const handleShow = () => {
            if (isModalOpenRef.current)
                return;
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            if (couShouldOpen(sdk, storeSettings, storeOffers, storeStyles)) {
                setIsModalOpen(true);
            }
        };
        store.on('change', handleStoreChange);
        store.on('offers-updated', handleStoreChange);
        store.on('settings-updated', handleStoreChange);
        store.on('show', handleShow);
        handleStoreChange();
        setUserAttributes((sdk.userAttributes ?? {}));
        return () => {
            store.off('change', handleStoreChange);
            store.off('offers-updated', handleStoreChange);
            store.off('settings-updated', handleStoreChange);
            store.off('show', handleShow);
        };
    }, [ctx]);
    useEffect(() => {
        setCurrentIndex(i => offers.length === 0 ? 0 : Math.min(i, Math.max(0, offers.length - 1)));
    }, [offers]);
    const currentOffer = offers[currentIndex] ?? null;
    const [isInView, setViewRef] = useIsInView({
        isEmbedded: settings?.embedded?.enabled === true,
        resetKey: currentOffer?.id,
    });
    const currentSaveState = currentOffer
        ? (saveStates[String(currentOffer.id)] ??
            (currentOffer.alreadySaved ? 'saved' : 'idle'))
        : 'idle';
    useEffect(() => {
        if (!ctx || !currentOffer) {
            return;
        }
        const { sdk, store } = ctx;
        const handleClose = () => {
            setIsModalOpen(false);
            fireBeacon('close', currentOffer);
            sdk.sendMessage(PLATFORM_MESSAGES.ADS_CLOSED.name, PLATFORM_MESSAGES.ADS_CLOSED);
        };
        store.setModalCloseCallback(handleClose);
    }, [ctx, currentOffer]);
    useEffect(() => {
        const root = rootRef.current;
        if (!root || !ctx || !settings || !currentOffer) {
            return;
        }
        const sendPlatformMessage = (eventName, eventData) => ctx.sdk.sendMessage(eventName, eventData);
        const totalOffers = offers.length;
        const isLastOffer = currentIndex >= totalOffers - 1;
        const handleCloseModal = () => {
            ctx.store.closeModal();
        };
        // An embedded COU lives inline in the publisher's page, so exhausting the
        // deck must not tear it out of their layout. It stays mounted and
        // interactive, same as an embedded SOU (SingleOfferUnit's handleClose).
        // The template-authored `close` control is deliberately exempt: a publisher
        // who puts a close button in the template means it.
        const shouldCloseWhenOffersExhausted = !settings.embedded?.enabled;
        const handleCta = () => {
            const offerData = {
                ...PLATFORM_MESSAGES.AD_TAKEN,
                ad_id: currentOffer.id,
                target_url: currentOffer.click_url,
                current: currentIndex + 1,
                total: totalOffers,
            };
            sendPlatformMessage(PLATFORM_MESSAGES.AD_TAKEN.name, offerData);
            sendPlatformMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
                ...PLATFORM_MESSAGES.URL_CLICKED,
                target_url: currentOffer.click_url,
            });
            const url = applyCacheBuster(currentOffer.click_url);
            window.open(url || currentOffer.click_url || '', '_blank');
            if (isLastOffer) {
                if (shouldCloseWhenOffersExhausted) {
                    setTimeout(() => {
                        handleCloseModal();
                    }, 1500);
                }
            }
            else {
                setCurrentIndex(prev => prev + 1);
            }
        };
        const handleNoThanks = () => {
            const offerData = {
                ...PLATFORM_MESSAGES.AD_NOT_TAKEN,
                ad_id: currentOffer.id,
                current: currentIndex + 1,
                total: totalOffers,
            };
            sendPlatformMessage(PLATFORM_MESSAGES.AD_NOT_TAKEN.name, offerData);
            fireBeacon('no_thanks_click', currentOffer);
            if (isLastOffer) {
                if (shouldCloseWhenOffersExhausted) {
                    handleCloseModal();
                }
                return;
            }
            setCurrentIndex(prev => prev + 1);
        };
        const handleOfferwall = () => {
            if (!settings.enable_offerwall ||
                !settings.offerwall_show_cta ||
                (settings.enable_user_selected_perks &&
                    settings.enable_usp &&
                    userAttributes.pub_user_id)) {
                return;
            }
            const apiPayload = {
                ...userAttributes,
                sessionId: ctx.store.getSessionId(),
                accountId: ctx.sdk.accountId,
                awinAdvertiserId: ctx.sdk.awinAdvertiserId,
                campaignId: ctx.sdk.campaignId,
                pub_user_id: userAttributes.pub_user_id,
                theme_id: ctx.sdk.theme_id,
            };
            const url = buildOfferwallUrl(apiPayload);
            if (!url) {
                return;
            }
            if (!settings.open_overlay_offerwall) {
                window.open(url, '_blank');
                return;
            }
            window.open(url, '_blank');
        };
        const handleSaveForLater = async () => {
            if (currentSaveState !== 'idle' ||
                !isPerksWalletEligible(currentOffer, settings, userAttributes)) {
                return;
            }
            const key = String(currentOffer.id);
            /** Only the PWAAS path is in-page and awaitable, so only it shows "Saving…". */
            const isPwaas = usesPwaasSave(settings, userAttributes);
            if (isPwaas) {
                setSaveStates(prev => ({ ...prev, [key]: 'saving' }));
            }
            const result = await savePerksWalletOffer(currentOffer, settings, userAttributes);
            if (result === 'saved') {
                setSaveStates(prev => ({ ...prev, [key]: 'saved' }));
            }
            else if (isPwaas) {
                setSaveStates(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
        };
        const onClick = (e) => {
            const jumpTo = progressJumpTarget(e.target);
            if (jumpTo != null) {
                e.preventDefault();
                e.stopPropagation();
                if (jumpTo < offers.length) {
                    setCurrentIndex(jumpTo);
                }
                return;
            }
            const el = e.target?.closest('[data-ms-action]');
            if (!el) {
                return;
            }
            const action = el.getAttribute('data-ms-action')?.trim();
            if (!action) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            switch (action) {
                case 'cta':
                    handleCta();
                    break;
                case 'close':
                    handleCloseModal();
                    break;
                case 'no-thanks':
                    handleNoThanks();
                    break;
                case 'next': {
                    const loop = el.getAttribute('data-ms-loop') === 'true';
                    setCurrentIndex(prev => prev < totalOffers - 1
                        ? prev + 1
                        : loop && totalOffers > 1
                            ? 0
                            : prev);
                    break;
                }
                case 'prev': {
                    const loop = el.getAttribute('data-ms-loop') === 'true';
                    setCurrentIndex(prev => prev > 0
                        ? prev - 1
                        : loop && totalOffers > 1
                            ? totalOffers - 1
                            : prev);
                    break;
                }
                case 'offerwall':
                    handleOfferwall();
                    break;
                case 'save-for-later':
                    handleSaveForLater();
                    break;
                default:
                    break;
            }
        };
        /**
         * Enter / Space on a progress tile, matching the click. The tiles the SDK made
         * focusable are the only ones that answer here, so a theme's own focusable markup
         * inside a tile keeps its keys.
         */
        const onKeyDown = (e) => {
            if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') {
                return;
            }
            const jumpTo = progressKeyJumpTarget(e.target);
            if (jumpTo == null) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            if (jumpTo < offers.length) {
                setCurrentIndex(jumpTo);
            }
        };
        root.addEventListener('click', onClick);
        root.addEventListener('keydown', onKeyDown);
        return () => {
            root.removeEventListener('click', onClick);
            root.removeEventListener('keydown', onKeyDown);
        };
    }, [
        ctx,
        settings,
        currentOffer,
        currentIndex,
        offers.length,
        userAttributes,
        currentSaveState,
    ]);
    useEffect(() => {
        const root = rootRef.current;
        if (!root || !ctx || !currentOffer || !isModalOpen) {
            return;
        }
        const { sdk } = ctx;
        const layout = effectiveCustomLayout(sdk, settings);
        const totalOffers = offers.length;
        if (layout?.render) {
            try {
                const macroAttrs = macroAttrsForOffer(currentOffer, userAttributes, styles, EVERY_KEY_EMITS_RAW_HTML);
                /** A `render` function has no template string to plan regions from. */
                progressRef.current = null;
                releaseProgressRegions(root, null);
                morphInnerHTML(root, layout.render(currentOffer, macroAttrs));
                applyOfferNavVisibility(root, currentIndex, totalOffers);
            }
            catch (err) {
                debug.error('customLayout.render failed:', err);
                root.innerHTML = '';
            }
            return;
        }
        const tpl = resolveMarkupTemplate(sdk, settings);
        if (!tpl) {
            debug.warn(`${sdk.instanceName}: no template (preset, customLayout.template, templateSelector, or API data.template).`);
            root.innerHTML = '';
            return;
        }
        const expanded = expandCouDescriptionMacrosToRawHtml(tpl);
        const rawKeys = collectRawMacroKeys(expanded);
        const emitsRawHtml = key => rawKeys.has(key);
        const macroAttrs = macroAttrsForOffer(currentOffer, userAttributes, styles, emitsRawHtml);
        /**
         * A progress unit is one offer's markup, so it resolves against that offer, not
         * the one on screen. Planned off the raw template: by the time the region reaches
         * the DOM its macros have already been resolved against whoever is on screen.
         */
        const plans = planProgressRegions(root.ownerDocument, expanded);
        progressRef.current = {
            plans,
            resolveUnit: (unitTemplate, offer) => injectMacros(unitTemplate, macroAttrsForOffer(offer, userAttributes, styles, emitsRawHtml)),
        };
        releaseProgressRegions(root, plans);
        morphInnerHTML(root, injectMacros(expanded, macroAttrs));
        applyOfferNavVisibility(root, currentIndex, totalOffers);
    }, [
        ctx,
        currentOffer,
        userAttributes,
        styles,
        isModalOpen,
        currentIndex,
        offers.length,
        ctx?.sdk.lastResponseTemplate,
        settings,
        ctx?.sdk.customLayout?.preset,
        ctx?.sdk.customLayout?.template,
        ctx?.sdk.customLayout?.templateSelector,
        ctx?.sdk.customLayout?.render,
    ]);
    /**
     * Declared after the morph effect so it always runs on the freshly-morphed DOM,
     * and deliberately without a dependency array: the save button's label has to
     * settle both when the template re-renders (a superset of the morph effect's
     * deps) and when only `saveStates` moved, which that effect does not watch.
     * The body is two `querySelectorAll`s and is idempotent.
     */
    useEffect(() => {
        const root = rootRef.current;
        if (!root || !settings || !currentOffer || !isModalOpen) {
            return;
        }
        applySaveForLaterState(root, currentOffer, settings, userAttributes, currentSaveState);
    });
    /**
     * Same placement rule as `applySaveForLaterState`: declared after the morph effect
     * so it runs on freshly-morphed DOM, and dependency-free because a region also has
     * to settle when only `isMobile` moved, which that effect does not watch. The body
     * is one `querySelectorAll` plus attribute writes, and is idempotent.
     */
    useEffect(() => {
        const root = rootRef.current;
        const progress = progressRef.current;
        if (!root || !settings || !isModalOpen || !progress) {
            return;
        }
        progressRootRef.current = root;
        applyProgressRegions(root, {
            plans: progress.plans,
            offers,
            currentIndex,
            settings,
            styles,
            isMobile,
            resolveUnit: progress.resolveUnit,
            onJump: setCurrentIndex,
        });
    });
    useEffect(() => () => {
        const root = progressRootRef.current;
        if (root) {
            teardownProgressRegions(root);
        }
    }, []);
    useEffect(() => {
        if (!ctx || !isModalOpen || offers.length < 2) {
            return;
        }
        const { sdk } = ctx;
        const layout = effectiveCustomLayout(sdk, settings);
        const tpl = layout?.render ? null : resolveMarkupTemplate(sdk, settings);
        if (!layout?.render && !tpl) {
            return;
        }
        const expanded = tpl ? expandCouDescriptionMacrosToRawHtml(tpl) : null;
        const rawKeys = expanded ? collectRawMacroKeys(expanded) : null;
        const emitsRawHtml = rawKeys
            ? key => rawKeys.has(key)
            : EVERY_KEY_EMITS_RAW_HTML;
        const urls = [];
        for (const offer of offers) {
            const macroAttrs = macroAttrsForOffer(offer, userAttributes, styles, emitsRawHtml);
            let html = '';
            if (layout?.render) {
                try {
                    html = layout.render(offer, macroAttrs);
                }
                catch {
                    html = '';
                }
            }
            else if (expanded) {
                html = injectMacros(expanded, macroAttrs);
            }
            if (html) {
                urls.push(...extractImageUrls(html));
            }
        }
        preloadImages(urls);
    }, [ctx, isModalOpen, offers, settings, styles, userAttributes]);
    const hasPixelFired = (offerId) => pixelsFired.has(offerId);
    const markPixelFired = (offerId) => {
        setPixelsFired(prev => new Set(prev).add(offerId));
    };
    const handleCloseFromModal = () => {
        ctx?.store.closeModal();
    };
    if (!settings || !styles || !currentOffer) {
        return _jsx(Fragment, {});
    }
    return (_jsxs(OfferModal, { isOpen: isModalOpen, onClose: handleCloseFromModal, settings: settings, styles: styles, offerChrome: 'template', children: [currentOffer.pixel && isInView && !hasPixelFired(currentOffer.id) && (_jsx("img", { style: { display: 'none' }, src: applyCacheBuster(currentOffer.pixel), alt: 'Pixel', onLoad: () => markPixelFired(currentOffer.id), onError: () => markPixelFired(currentOffer.id) })), currentOffer.adv_pixel_url &&
                isInView &&
                !hasPixelFired(`${currentOffer.id}_adv`) && (_jsx("img", { style: { display: 'none' }, src: applyCacheBuster(currentOffer.adv_pixel_url), alt: 'Pixel', onLoad: () => markPixelFired(`${currentOffer.id}_adv`), onError: () => markPixelFired(`${currentOffer.id}_adv`) })), _jsx("div", { ref: el => {
                    rootRef.current = el;
                    setViewRef(el);
                }, "data-ms-custom-root": true })] }));
}
